/**
 * 3D model editor (EAA-1642) — title/instructions, model uploader, viewer settings.
 *
 * Upload requires a saved Content (the backend links the asset by content_id),
 * mirroring the SCORM editor. The stored linkage (assetId + contentId) is written
 * into the content `data` so the player can mint one-time serve tokens at runtime.
 */

import * as React from 'react';
import { Input, Label, Textarea, Checkbox, Button, cn } from '@ai-content-hub/content-kit';
import { useLocale } from '@ai-content-hub/content-core';
import { bytesToMb, resolveLimitBytes, useUploadLimits } from '@ai-content-hub/shared-config';
import { Box, Upload, Trash2, Sun, Film, RefreshCw, MousePointerClick } from 'lucide-react';
import type { Content3DData, Content3DConfig, LightPreset, Model3DObjectInteraction } from './schema';
import { defaultContent3DData } from './schema';
import {
    uploadModel3D,
    deleteModel3D,
    uploadModel3DEnvironment,
    setModel3DTenantId,
    fetchModel3DOutline,
    saveModel3DOutline,
} from './api';
import { extractModelOutline, extractOutlineFromFile, type Model3DOutline } from './lib/modelOutline';
import { loadModel3D } from './lib/loadModel';
import { ObjectInteractionList } from './components/ObjectInteractionList';

export interface Content3DEditProps {
    value: Content3DData;
    onChange: (value: Content3DData) => void;
    contentId?: string;
    config?: Content3DConfig;
    disabled?: boolean;
    className?: string;
    errors?: Record<string, string>;
    /** EAA-1642: optional tenant_id for staff JWTs (passed by parent if available) */
    tenantId?: string;
}

type UploadState = 'idle' | 'uploading' | 'error';

const ACCEPTED = '.glb,.gltf';

/** EAA-1212: the server's shipped 3D-model cap, used until the configured one is known. */
const MODEL3D_FALLBACK_MAX_BYTES = 200 * 1024 * 1024;



function isAcceptedFile(name: string): boolean {
    const lower = name.toLowerCase();
    return lower.endsWith('.glb') || lower.endsWith('.gltf');
}

export const Content3DEdit: React.FC<Content3DEditProps> = ({
    value,
    onChange,
    contentId: propContentId,
    config,
    disabled = false,
    className,
    errors,
    tenantId,
}) => {
    const { t } = useLocale({ config: config?.i18n });
    const { model3dMaxSizeMb } = useUploadLimits();
    const modelMaxBytes = resolveLimitBytes(MODEL3D_FALLBACK_MAX_BYTES, model3dMaxSizeMb);
    const data: Content3DData = { ...defaultContent3DData, ...value };
    const contentId = (propContentId ?? data.contentId ?? '').trim();

    const [state, setState] = React.useState<UploadState>('idle');
    const [errorMsg, setErrorMsg] = React.useState('');
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [envState, setEnvState] = React.useState<UploadState>('idle');
    const [envErrorMsg, setEnvErrorMsg] = React.useState('');
    const envFileInputRef = React.useRef<HTMLInputElement>(null);

    // Set module-level tenant context so all API calls (token, delete) resolve it.
    // EAA-1642: model outline (clip names + clickable object names) and the
    // object → clip interaction authoring built on top of it.
    const [outlineState, setOutlineState] = React.useState<'idle' | 'loading' | 'error'>('idle');
    const fetchedOutlineFor = React.useRef<string>('');

    React.useEffect(() => { setModel3DTenantId(tenantId); }, [tenantId]);

    const update = (patch: Partial<Content3DData>) => onChange({ ...data, ...patch });

    /**
     * EAA-1642: the async handlers below (upload, scan, the outline fetch) can
     * take seconds, and `data` is captured at render. Merging their patch into
     * the render-time snapshot would silently discard anything the author typed
     * while they were in flight — so those paths merge into the LATEST data.
     */
    const dataRef = React.useRef(data);
    dataRef.current = data;
    const updateLatest = (patch: Partial<Content3DData>) =>
        onChange({ ...dataRef.current, ...patch });

    // Memoised so the derived maps/lists below keep stable identities — a new
    // `[]` literal every render would defeat them.
    const animationNames = React.useMemo(() => data.animationNames ?? [], [data.animationNames]);
    const objectNames = React.useMemo(() => data.objectNames ?? [], [data.objectNames]);

    const assignedByObject = React.useMemo(() => {
        const map = new Map<string, string>();
        (data.objectInteractions ?? []).forEach(({ objectName, clipName }) => {
            if (objectName && clipName) map.set(objectName, clipName);
        });
        return map;
    }, [data.objectInteractions]);

    /**
     * Fold a freshly-read outline into the content data.
     *
     * Existing interactions are pruned against it: a re-upload can drop a clip
     * or rename a node, and a mapping pointing at something the model no longer
     * has would silently never fire — worse than being told it is gone.
     */
    const outlinePatch = (outline: Model3DOutline): Partial<Content3DData> => {
        const clips = new Set(outline.animations);
        const objects = new Set(outline.objects);
        return {
            animationNames: outline.animations,
            objectNames: outline.objects,
            objectInteractions: (dataRef.current.objectInteractions ?? []).filter(
                (i) => objects.has(i.objectName) && clips.has(i.clipName),
            ),
        };
    };

    /** Read the model once and store the result on the asset for every later session. */
    const handleScan = async () => {
        const assetId = (data.assetId ?? '').trim();
        if (!assetId) return;
        setOutlineState('loading');
        try {
            const loaded = await loadModel3D(assetId);
            const outline = extractModelOutline(loaded.scene, loaded.animations);
            loaded.dispose();
            updateLatest(outlinePatch(outline));
            void saveModel3DOutline(assetId, outline);
            setOutlineState('idle');
        } catch {
            setOutlineState('error');
        }
    };

    const handleInteractionsChange = (next: Model3DObjectInteraction[]) =>
        update({ objectInteractions: next });



    const handleFile = async (file: File) => {
        if (!isAcceptedFile(file.name)) {
            setErrorMsg(t('uploadOnlyGlb', 'Only .glb or .gltf files are supported.'));
            setState('error');
            return;
        }
        // EAA-1212: refuse an oversize model before sending it, using the
        // server-configured 3D cap rather than letting a full upload fail.
        if (file.size > modelMaxBytes) {
            setErrorMsg(
                t('uploadTooLarge', 'The model is larger than the {max}MB limit.').replace(
                    '{max}',
                    String(bytesToMb(modelMaxBytes))
                )
            );
            setState('error');
            return;
        }
        setState('uploading');
        setErrorMsg('');
        try {
            // EAA-1642: contentId is optional — a new/unsaved content has only a
            // temp id, so the asset is uploaded unlinked and referenced by assetId.
            // tenantId is also optional; if available, pass it for staff JWT context.
            const result = await uploadModel3D(file, contentId || undefined, tenantId);

            // EAA-1642: the File is still in memory, so read the outline from
            // those bytes rather than paying for a serve token and a second
            // download of a file that can be 50-100MB. It is written onto the
            // asset as well, so a later session reuses it with no parse at all.
            let outlineFields: Partial<Content3DData> = {
                animationNames: [],
                objectNames: [],
                objectInteractions: [],
            };
            try {
                const outline = await extractOutlineFromFile(file);
                outlineFields = {
                    animationNames: outline.animations,
                    objectNames: outline.objects,
                    objectInteractions: [], // a new model invalidates every old mapping
                };
                void saveModel3DOutline(result.asset_id, outline);
                setOutlineState('idle');
            } catch {
                // The upload itself succeeded — the author can still "Rescan model".
                setOutlineState('error');
            }
            fetchedOutlineFor.current = result.asset_id;

            updateLatest({
                assetId: result.asset_id,
                fileFormat: result.file_format,
                originalFilename: result.original_filename,
                contentId: contentId || undefined,
                ...outlineFields,
            });
            setState('idle');
        } catch (err) {
            setErrorMsg(err instanceof Error ? err.message : 'Upload failed');
            setState('error');
        }
    };

    const handleRemove = async () => {
        const assetId = (data.assetId ?? '').trim();
        try {
            if (assetId) await deleteModel3D(assetId);
        } catch {
            /* best-effort: still clear the local linkage below */
        }
        updateLatest({
            assetId: '',
            fileFormat: undefined,
            originalFilename: '',
            // EAA-1642: the outline and every mapping belong to the removed model.
            animationNames: [],
            objectNames: [],
            objectInteractions: [],
        });
        fetchedOutlineFor.current = '';
        setState('idle');
    };

    const hasModel = Boolean((data.assetId ?? '').trim());

    /**
     * EAA-1642: pull the outline the asset already carries.
     *
     * This is the "extract once" half of the feature — an author reopening
     * content, or pointing a second content item at the same asset, gets the
     * clip and object lists from the two dedicated asset fields and never
     * downloads the model. Only a model uploaded before this feature (or one
     * whose write failed) falls through to the explicit "Scan model" button.
     */
    React.useEffect(() => {
        const assetId = (data.assetId ?? '').trim();
        if (!assetId || fetchedOutlineFor.current === assetId) return;
        if ((data.animationNames?.length ?? 0) > 0 || (data.objectNames?.length ?? 0) > 0) {
            fetchedOutlineFor.current = assetId;
            return;
        }
        fetchedOutlineFor.current = assetId;
        let cancelled = false;
        void (async () => {
            try {
                const outline = await fetchModel3DOutline(assetId);
                if (cancelled) return;
                if (outline.animations.length === 0 && outline.objects.length === 0) return;
                updateLatest(outlinePatch(outline));
            } catch {
                // No stored outline (or an older backend) — the Scan button covers it.
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.assetId]);

    return (
        <div className={cn('content-3d-edit flex flex-col gap-6 h-full min-h-0 overflow-y-auto p-1', className)}>
            <div className="content-3d-edit-field space-y-2">
                <Label htmlFor="content-3d-title">{t('titleLabel', 'Title')}</Label>
                <Input
                    id="content-3d-title"
                    value={data.title ?? ''}
                    disabled={disabled}
                    placeholder={t('titlePlaceholder', '3D model title')}
                    onChange={(e) => update({ title: e.target.value })}
                    className="content-3d-edit-title"
                />
            </div>

            <div className="content-3d-edit-field space-y-2">
                <Label htmlFor="content-3d-desc">{t('descLabel', 'Instructions (optional)')}</Label>
                <Textarea
                    id="content-3d-desc"
                    value={data.description ?? ''}
                    disabled={disabled}
                    placeholder={t('descPlaceholder', 'Short instructions')}
                    onChange={(e) => update({ description: e.target.value })}
                    className="content-3d-edit-description"
                />
            </div>

            {/* Model uploader */}
            <div className="content-3d-edit-field space-y-2">
                <Label>{t('contentTab', 'Model')}</Label>
                {hasModel ? (
                    <div className="content-3d-edit-model flex items-center gap-3 rounded-sm bg-muted/30 p-3 border border-border">
                        <Box className="h-5 w-5 text-primary shrink-0" />
                        <div className="min-w-0 flex-1">
                            <div className="content-3d-edit-model-name truncate text-sm font-medium">
                                {data.originalFilename || data.assetId}
                            </div>
                            <div className="content-3d-edit-model-format text-xs text-muted-foreground uppercase">
                                {data.fileFormat}
                            </div>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={disabled}
                            onClick={handleRemove}
                            className="content-3d-edit-remove cursor-pointer text-destructive"
                        >
                            <Trash2 className="h-4 w-4" />
                            {t('uploadDelete', 'Remove')}
                        </Button>
                    </div>
                ) : (
                    <button
                        type="button"
                        disabled={disabled || state === 'uploading'}
                        onClick={() => fileInputRef.current?.click()}
                        className={cn(
                            'content-3d-edit-dropzone flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border/60 bg-muted/20 p-8 text-center cursor-pointer transition-colors hover:bg-muted/40',
                            state === 'uploading' && 'opacity-70 cursor-wait',
                        )}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                            e.preventDefault();
                            const file = e.dataTransfer.files?.[0];
                            if (file) handleFile(file);
                        }}
                    >
                        <Upload className="h-8 w-8 text-muted-foreground" />
                        <span className="text-sm font-medium">
                            {state === 'uploading'
                                ? t('uploadUploading', 'Uploading…')
                                : t('uploadDropzone', 'Drag & drop a .glb / .gltf file, or click to browse')}
                        </span>
                        <span className="text-xs text-muted-foreground">{t('uploadHint', 'Supported: GLB, glTF')}</span>
                    </button>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED}
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFile(file);
                        e.target.value = '';
                    }}
                />
                {state === 'error' && errorMsg ? (
                    <p className="content-3d-edit-upload-error text-sm text-destructive">{errorMsg}</p>
                ) : null}
                {errors?.assetId ? (
                    <p className="content-3d-edit-error text-sm text-destructive">{errors.assetId}</p>
                ) : null}
            </div>

            {/* Viewer settings */}
            {hasModel ? (
                <div className="content-3d-edit-animations space-y-4 border-t border-border pt-4">
                    <div className="flex items-center justify-between gap-3">
                        <Label className="flex items-center gap-2">
                            <Film className="h-4 w-4 text-primary" />
                            {t('animationsLabel', 'Animations')}
                        </Label>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={disabled || outlineState === 'loading'}
                            onClick={handleScan}
                            className="content-3d-edit-scan cursor-pointer"
                        >
                            <RefreshCw className={cn('h-4 w-4', outlineState === 'loading' && 'animate-spin')} />
                            {outlineState === 'loading'
                                ? t('animationsScanning', 'Reading the model\u2026')
                                : animationNames.length > 0 || objectNames.length > 0
                                    ? t('animationsRescan', 'Rescan model')
                                    : t('animationsScan', 'Scan model for animations & objects')}
                        </Button>
                    </div>

                    {animationNames.length > 0 ? (
                        <div className="content-3d-edit-animation-list flex flex-wrap gap-2">
                            {animationNames.map((name) => (
                                <span
                                    key={name}
                                    title={name}
                                    className="content-3d-edit-animation inline-flex max-w-[14rem] items-center gap-1.5 truncate rounded-sm border border-border bg-muted/30 px-2 py-1 text-xs"
                                >
                                    <Film className="h-3 w-3 shrink-0 text-muted-foreground" />
                                    <span className="truncate">{name}</span>
                                </span>
                            ))}
                        </div>
                    ) : (
                        <p className="text-xs text-muted-foreground">
                            {outlineState === 'error'
                                ? t('animationsScanError', 'Could not read the model structure.')
                                : objectNames.length > 0
                                    ? t('animationsNone', 'This model has no embedded animations.')
                                    : t('animationsScanHint', 'Downloads the model once to read its clip and object names.')}
                        </p>
                    )}

                    {animationNames.length > 0 ? (
                        <div className="flex items-start gap-3">
                            <Checkbox
                                id="content-3d-show-anim-buttons"
                                checked={data.showAnimationButtons ?? true}
                                disabled={disabled}
                                onCheckedChange={(v) => update({ showAnimationButtons: v === true })}
                                className="content-3d-edit-show-anim-buttons mt-1"
                            />
                            <Label htmlFor="content-3d-show-anim-buttons" className="text-sm leading-relaxed cursor-pointer">
                                {t('showAnimationButtonsLabel', 'Show an animation button per clip below the view')}
                            </Label>
                        </div>
                    ) : null}

                    {animationNames.length > 0 ? (
                        <div className="flex items-start gap-3">
                            <Checkbox
                                id="content-3d-show-timeline"
                                checked={data.showAnimationTimeline ?? false}
                                disabled={disabled}
                                onCheckedChange={(v) => update({ showAnimationTimeline: v === true })}
                                className="content-3d-edit-show-timeline mt-1"
                            />
                            <Label htmlFor="content-3d-show-timeline" className="text-sm leading-relaxed cursor-pointer">
                                {t('showAnimationTimelineLabel', 'Show the animation timeline over the view')}
                            </Label>
                        </div>
                    ) : null}

                    {animationNames.length > 0 ? (
                        <div className="flex items-start gap-3">
                            <Checkbox
                                id="content-3d-loop-animation"
                                checked={data.loopAnimation ?? false}
                                disabled={disabled}
                                onCheckedChange={(v) => update({ loopAnimation: v === true })}
                                className="content-3d-edit-loop-animation mt-1"
                            />
                            <Label htmlFor="content-3d-loop-animation" className="text-sm leading-relaxed cursor-pointer">
                                {t('loopAnimationLabel', 'Replay the animation continuously')}
                            </Label>
                        </div>
                    ) : null}

                    {animationNames.length > 0 ? (
                        <div className="content-3d-edit-interactions space-y-3 rounded-sm border border-border bg-muted/20 p-3">
                            <div className="space-y-1">
                                <Label className="flex items-center gap-2">
                                    <MousePointerClick className="h-4 w-4 text-primary" />
                                    {t('interactionsLabel', 'Object interactions')}
                                    {assignedByObject.size > 0 ? (
                                        <span className="text-xs font-normal text-muted-foreground">
                                            {t('interactionsAssigned', 'Assigned ({{count}})').replace(
                                                '{{count}}',
                                                String(assignedByObject.size),
                                            )}
                                        </span>
                                    ) : null}
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                    {t(
                                        'interactionsHint',
                                        'Pick an animation for an object; clicking that object in the player plays it.',
                                    )}
                                </p>
                            </div>

                            <ObjectInteractionList
                                objects={objectNames}
                                animations={animationNames}
                                interactions={data.objectInteractions ?? []}
                                onChange={handleInteractionsChange}
                                disabled={disabled}
                                assetId={(data.assetId ?? '').trim()}
                                t={t}
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}

            <div className="content-3d-edit-settings space-y-4 border-t border-border pt-4">
                <div className="flex items-start gap-3">
                    <Checkbox
                        id="content-3d-autorotate"
                        checked={data.autoRotate ?? false}
                        disabled={disabled}
                        onCheckedChange={(v) => update({ autoRotate: v === true })}
                        className="content-3d-edit-autorotate mt-1"
                    />
                    <Label htmlFor="content-3d-autorotate" className="text-sm leading-relaxed cursor-pointer">
                        {t('autoRotateLabel', 'Auto-rotate the model')}
                    </Label>
                </div>
                <div className="flex items-start gap-3">
                    <Checkbox
                        id="content-3d-autoplay-anim"
                        checked={data.autoPlayAnimation ?? false}
                        disabled={disabled}
                        onCheckedChange={(v) => update({ autoPlayAnimation: v === true })}
                        className="content-3d-edit-autoplay-anim mt-1"
                    />
                    <Label htmlFor="content-3d-autoplay-anim" className="text-sm leading-relaxed cursor-pointer">
                        {t('autoPlayAnimationLabel', 'Auto-play animations')}
                    </Label>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="content-3d-light-preset">{t('lightPresetLabel', 'Lighting')}</Label>
                    <select
                        id="content-3d-light-preset"
                        value={data.lightPreset ?? 'studio'}
                        disabled={disabled}
                        onChange={(e) => {
                            const val = e.target.value as LightPreset | 'custom';
                            if (val === 'custom') {
                                update({ lightPreset: 'studio' }); // keep a fallback preset
                            } else {
                                update({ lightPreset: val, environmentAssetId: undefined, environmentFilename: undefined });
                            }
                        }}
                        className="content-3d-edit-light-preset flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <option value="studio">{t('lightPresetStudio', 'Studio (bright)')}</option>
                        <option value="outdoor">{t('lightPresetOutdoor', 'Outdoor (sunlight)')}</option>
                        <option value="soft">{t('lightPresetSoft', 'Soft (even)')}</option>
                        <option value="neutral">{t('lightPresetNeutral', 'Neutral')}</option>
                    </select>
                </div>
                {/* HDR environment map upload */}
                <div className="space-y-2">
                    <Label>{t('envUploadLabel', 'Environment Map')}</Label>
                    {(data.environmentAssetId ?? '').trim() ? (
                        <div className="content-3d-edit-env flex items-center gap-3 rounded-sm bg-muted/30 p-3 border border-border">
                            <Sun className="h-5 w-5 text-amber-500 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="content-3d-edit-env-name truncate text-sm font-medium">
                                    {data.environmentFilename || 'environment.hdr'}
                                </div>
                                <div className="text-xs text-muted-foreground uppercase">HDR</div>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={disabled}
                                onClick={async () => {
                                    const envId = (data.environmentAssetId ?? '').trim();
                                    try { if (envId) await deleteModel3D(envId); } catch { /* best-effort */ }
                                    update({ environmentAssetId: '', environmentFilename: '' });
                                }}
                                className="content-3d-edit-env-remove cursor-pointer text-destructive"
                            >
                                <Trash2 className="h-4 w-4" />
                                {t('envRemove', 'Remove')}
                            </Button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            disabled={disabled || envState === 'uploading'}
                            onClick={() => envFileInputRef.current?.click()}
                            className={cn(
                                'content-3d-edit-env-dropzone flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border/60 bg-muted/20 p-5 text-center cursor-pointer transition-colors hover:bg-muted/40',
                                envState === 'uploading' && 'opacity-70 cursor-wait',
                            )}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={async (e) => {
                                e.preventDefault();
                                const file = e.dataTransfer.files?.[0];
                                if (!file) return;
                                if (!file.name.toLowerCase().endsWith('.hdr')) {
                                    setEnvErrorMsg('Only .hdr files are supported.');
                                    setEnvState('error');
                                    return;
                                }
                                setEnvState('uploading');
                                setEnvErrorMsg('');
                                try {
                                    const result = await uploadModel3DEnvironment(file, contentId || undefined, tenantId);
                                    updateLatest({ environmentAssetId: result.asset_id, environmentFilename: result.original_filename });
                                    setEnvState('idle');
                                } catch (err) {
                                    setEnvErrorMsg(err instanceof Error ? err.message : 'Upload failed');
                                    setEnvState('error');
                                }
                            }}
                        >
                            <Sun className="h-6 w-6 text-muted-foreground" />
                            <span className="text-sm font-medium">
                                {envState === 'uploading'
                                    ? t('envUploading', 'Uploading…')
                                    : t('envUploadDropzone', 'Upload .hdr environment map')}
                            </span>
                            <span className="text-xs text-muted-foreground">{t('envUploadHint', 'Supported: HDR (Radiance)')}</span>
                        </button>
                    )}
                    <input
                        ref={envFileInputRef}
                        type="file"
                        accept=".hdr"
                        className="hidden"
                        onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            e.target.value = '';
                            if (!file.name.toLowerCase().endsWith('.hdr')) {
                                setEnvErrorMsg('Only .hdr files are supported.');
                                setEnvState('error');
                                return;
                            }
                            setEnvState('uploading');
                            setEnvErrorMsg('');
                            try {
                                const result = await uploadModel3DEnvironment(file, contentId || undefined, tenantId);
                                updateLatest({ environmentAssetId: result.asset_id, environmentFilename: result.original_filename });
                                setEnvState('idle');
                            } catch (err) {
                                setEnvErrorMsg(err instanceof Error ? err.message : 'Upload failed');
                                setEnvState('error');
                            }
                        }}
                    />
                    {envState === 'error' && envErrorMsg ? (
                        <p className="text-sm text-destructive">{envErrorMsg}</p>
                    ) : null}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="content-3d-minview">{t('minViewSecondsLabel', 'Minimum viewing time (seconds, 0 = off)')}</Label>
                    <Input
                        id="content-3d-minview"
                        type="number"
                        min={0}
                        step={1}
                        value={data.minViewSeconds ?? 0}
                        disabled={disabled}
                        onChange={(e) => {
                            const n = Number(e.target.value);
                            update({ minViewSeconds: Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0 });
                        }}
                        className="content-3d-edit-minview w-36"
                    />
                </div>
            </div>
        </div>
    );
};
